'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';

const caro01 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_01.webp`;
const caro02 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_02.webp`;
const caro03 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_03.webp`;
const caro04 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_04.webp`;
const caro05 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_05.webp`;
const caro06 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_06.webp`;
const caro07 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_07.webp`;
const caro08 = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/caro_08.webp`;

const ShowcaseCarousel = () => {
  const items = [
    { label: 'View 1', img: caro01 },
    { label: 'View 2', img: caro02 },
    { label: 'View 3', img: caro03 },
    { label: 'View 4', img: caro04 },
    { label: 'View 5', img: caro05 },
    { label: 'View 6', img: caro06 },
    { label: 'View 7', img: caro07 },
    { label: 'View 8', img: caro08 },
  ];

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [stepPx, setStepPx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  const trackRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // 화면에 보이는 카드 수를 고려한 maxIndex 계산
  const maxIndex = Math.max(0, items.length - visibleCount);
  // index가 maxIndex를 초과하지 않도록 보정 (리사이즈 시)
  const safeIndex = Math.min(index, maxIndex);
  const isAtStart = safeIndex === 0;
  const isAtEnd = safeIndex >= maxIndex;

  const updateStep = useCallback(() => {
    if (!trackRef.current || !cardRef.current) return;
    const gap = 16;
    const cardWidth = cardRef.current.offsetWidth;
    setStepPx(cardWidth + gap);

    // 화면에 보이는 카드 수 계산
    const containerWidth = trackRef.current.offsetWidth;
    const count = Math.floor((containerWidth + gap) / (cardWidth + gap));
    setVisibleCount(Math.max(1, count));
  }, []);

  useEffect(() => {
    updateStep();
    const ro = new ResizeObserver(updateStep);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [updateStep]);

  const next = useCallback(() => {
    setIndex(prev => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);

  const prev = useCallback(() => {
    setIndex(prev => Math.max(prev - 1, 0));
  }, []);

  // 자동 재생 (끝에 도달하면 처음으로)
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(timer);
  }, [paused, maxIndex]);

  const translateX = stepPx ? `-${safeIndex * stepPx}px` : '0';

  return (
    <section className='pt-32 pb-20 overflow-hidden'>
      <div className='text-center mb-12 px-6'>
        <h2
          className='font-display font-black tracking-[-0.04em] leading-[1.1]'
          style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
        >
          This is what <span className='gradient-text'>Tindlo</span> looks like.
        </h2>
      </div>

      <div
        className='relative mx-auto min-w-[300px] px-4 sm:px-6 w-full lg:max-w-[70%]'
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className='overflow-hidden rounded-(--radius)' ref={trackRef}>
          <div
            className='flex gap-4 transition-transform duration-500'
            style={{
              transform: `translateX(${translateX})`,
              transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)',
            }}
          >
            {items.map((item, i) => (
              <div
                key={i}
                ref={i === 0 ? cardRef : undefined}
                className='shrink-0 rounded-(--radius) overflow-hidden border border-border bg-card hover:scale-[1.03] transition-transform w-[78%] min-w-[78%] sm:w-[calc((100%-16px)/2)] sm:min-w-[calc((100%-16px)/2)] md:w-[calc((100%-32px)/3)] md:min-w-[calc((100%-32px)/3)] lg:w-[calc((100%-48px)/4)] lg:min-w-[calc((100%-48px)/4)]'
                style={{ aspectRatio: '3/4' }}
              >
                <Image
                  src={item.img}
                  alt={item.label}
                  className='w-full h-full object-cover'
                  width={390}
                  height={520}
                  sizes='(max-width: 640px) 78vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  loading={i < 4 ? 'eager' : 'lazy'}
                  priority={i < 2}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Arrows */}
        <div className='flex justify-center gap-4 mt-8'>
          <button
            onClick={prev}
            disabled={isAtStart}
            className='w-12 h-12 rounded-full border-[1.5px] border-border bg-card flex items-center justify-center transition-all text-muted-foreground cursor-pointer hover:border-[hsl(263,84%,55%)] hover:text-[hsl(263,84%,55%)] disabled:opacity-40 disabled:pointer-events-none'
            aria-label='Previous slide'
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            disabled={isAtEnd}
            className='w-12 h-12 rounded-full border-[1.5px] border-border bg-card flex items-center justify-center transition-all text-muted-foreground cursor-pointer hover:border-[hsl(263,84%,55%)] hover:text-[hsl(263,84%,55%)] disabled:opacity-40 disabled:pointer-events-none'
            aria-label='Next slide'
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseCarousel;
